import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React, {useEffect} from 'react';
import {useRectEffect} from 'react-viewport-utils';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {
  BaseProductTile,
  BaseProductTileProps,
} from '../BaseProductTile/BaseProductTile';
import {
  AddToBasketCtaContainer,
  ColorContainer,
  ProductDetailsWrapper,
} from '../BaseProductTile/ProductDetails';
import {ProductTileWrapper} from '../BaseProductTile/ProductTileWrapper';

// Wrapper component that decides which implementation to render.
// If enhanceSpeed is true, we render a lightweight version without any runtime measuring hooks
// (useEffect/useRectEffect). Otherwise we fall back to the expensive measuring variant.
export const InteractiveProductTileBase = (
  props: React.PropsWithChildren<BaseProductTileProps> & {
    className?: string;
    disableProductGalleryMode?: boolean;
    enhanceSpeed?: boolean;
  },
) => {
  const {enhanceSpeed, ...restProps} = props;

  // Branch early so we do NOT mount the measuring hooks when speed enhancement is enabled.
  if (enhanceSpeed) {
    return <GridInteractiveProductTile {...restProps} />;
  }
  return <ExpensiveInteractiveProductTile {...restProps} />;
};

const GridInteractiveProductTile = (
  props: React.PropsWithChildren<BaseProductTileProps> & {
    className?: string;
    disableProductGalleryMode?: boolean;
  },
) => {
  const {className, availableSizes, cta, disableProductGalleryMode, ...rest} =
    props;
  return (
    <InteractiveTileWrapper
      className={className}
      data-automatic-height-set={true}
    >
      <BaseProductTile
        {...rest}
        productTileGalleryMode={disableProductGalleryMode ? undefined : 'hover'}
        cta={
          <>
            <Sizes sizeLabels={availableSizes} />
            {cta}
          </>
        }
      />
    </InteractiveTileWrapper>
  );
};

// Expensive variant with measurement logic (original implementation).
const ExpensiveInteractiveProductTile = (
  props: React.PropsWithChildren<BaseProductTileProps> & {
    className?: string;
    disableProductGalleryMode?: boolean;
  },
) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const childRef = React.useRef<HTMLDivElement>(null);
  const childWidth = React.useRef<number | null>(null);

  const [height, setHeight] = React.useState<number | undefined>();

  const {className, availableSizes, cta, disableProductGalleryMode, ...rest} =
    props;

  const initAutomaticHeight = React.useCallback(() => {
    if (!ref.current) return;

    setHeight(ref.current.clientHeight);
  }, []);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      initAutomaticHeight();
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [initAutomaticHeight]);

  useRectEffect(
    rect => {
      if (!rect) return;
      if (!childWidth.current) {
        childWidth.current = rect.width;
        return;
      }
      if (rect.width !== childWidth.current) {
        setHeight(rect.height);
        childWidth.current = rect.width;
      }
    },
    childRef,
    {deferUpdateUntilIdle: true, priority: 'low'},
  );

  return (
    <InteractiveTileWrapper
      className={className}
      ref={ref}
      $height={height}
      data-interactive={Boolean(height)}
      data-automatic-height-set={false}
    >
      <div ref={childRef}>
        <BaseProductTile
          {...rest}
          productTileGalleryMode={
            disableProductGalleryMode ? undefined : 'hover'
          }
          cta={
            <>
              <Sizes sizeLabels={availableSizes} />
              {cta}
            </>
          }
        />
      </div>
    </InteractiveTileWrapper>
  );
};

const InteractiveTileWrapper = styled.div<{$height?: number}>`
  --defaultHeight: auto;
  position: relative;

  ${ColorContainer} {
    display: grid;
    opacity: 0;
    grid-template-rows: 0fr;
    padding: 0;
    transform: translateY(5px);
    transition: opacity 300ms ease-in, transform 300ms ease-in,
      grid-template-rows 200ms ease-in, padding 300ms ease-in-out,
      opacity 300ms ease;
  }

  ${ProductDetailsWrapper} {
    transition: transform 300ms ease-in-out;
    background-color: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(10px);
  }

  ${AddToBasketCtaContainer} {
    display: grid;
    transform: translateY(5px);
    grid-template-rows: 0fr;
    transition: opacity 300ms ease-in, transform 300ms ease-in,
      grid-template-rows 300ms linear, opacity 300ms ease;

    > * {
      min-width: 0;
    }
  }

  ${ProductTileWrapper} {
    box-sizing: border-box;
    border-radius: 4px;
    border: transparent solid 1px;

    &[data-enable-containment='true'] {
      contain-intrinsic-size: var(--tile-height);
    }
  }

  height: ${({$height}) => ($height ? `${$height}px` : 'var(--defaultHeight)')};

  &[data-automatic-height-set='true'] {
    /* Fallback to --defaultHeight when --tile-height is not defined upstream */
    height: var(--tile-height, var(--defaultHeight));
  }

  &[data-interactive='true'] {
    @media (pointer: fine) {
      &:hover {
        ${ProductTileWrapper} {
          z-index: 2;
          position: absolute;
          width: 100%;
          border-color: ${COLORS.Grey3};
          contain: content;
          contain-intrinsic-size: initial;
          content-visibility: visible;
          will-change: auto;
        }

        ${ColorContainer} {
          opacity: 1;
          grid-template-rows: 1fr;
          transform: translateY(0);
          padding: 8px 0 0 0;
        }

        ${AddToBasketCtaContainer} {
          opacity: 1;
          grid-template-rows: 1fr;
          transform: translateY(0);
        }

        ${ProductDetailsWrapper} {
          transform: translateY(-60px);
          margin-bottom: -60px;
        }
      }
    }
  }
`;

const Sizes = ({sizeLabels}: {sizeLabels?: string[]}) => {
  const {t} = useTranslation();

  if (!sizeLabels) {
    return null;
  }

  const hasLongSizeList =
    sizeLabels.length > 6 ||
    (sizeLabels.length > 4 &&
      sizeLabels.some(label => {
        return label?.includes('-') ?? false;
      }));

  return (
    <SizesContainer data-testid="SizesContainer">
      {hasLongSizeList ? (
        <>{t('available in many sizes')}</>
      ) : (
        <>{`${t('Available sizes')}: ${sizeLabels.join(', ')}`}</>
      )}
    </SizesContainer>
  );
};

const SizesContainer = styled.span`
  display: inline; // text-overflow: ellipsis does not work if this isn't specified explicitly
  font-size: ${FONT_SIZES.px10};
  color: ${COLORS.Grey1};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  transition: opacity 300ms ease-in, transform 300ms ease-in;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  direction: initial;
  margin-top: 8px;
  text-align: center;
`;
